import flatten from 'lodash/flatten';
import { NotAvailable } from '@common/i18n/constants';
import { TargetTab } from '@common/store';
import {
  type ChartRange,
  DataHistoryAggregation,
  DataHistoryResolution,
  GraphItemContextType,
  type GraphItemVisualisationType,
  type RegisterGroupVisibility,
  type ResolutionUnit,
  type Unit,
} from '@common/types/api';
import type Register from '@common/types/models/Register';
import { type BitMask16 } from '@common/utils/formatting/multiOptions';

export type SimpleSeriesType = 'line' | 'column' | 'area';

export interface DataHistoryConfiguration {
  range: ChartRange;
  resolution: DataHistoryResolution;
}

export interface GraphConfiguration {
  range?: ChartRange;
  resolution?: DataHistoryResolution;
  withComparison?: boolean;
}

export interface RegisterDataSeries extends Register {
  contextType?: GraphItemContextType;
  valueBitMask?: BitMask16;
  synthetic?: boolean;
  groupVisibilityId?: RegisterGroupVisibility;
  resolutionUnits?: ResolutionUnit[];
  resolution?: DataHistoryResolution;
  desiredAggregation?: DataHistoryAggregation;
  requestUnit?: Unit;
  range?: ChartRange;
  visualisationType?: GraphItemVisualisationType;
  colorIndex?: number;
  isForComparison?: boolean;
}

interface DataPoint extends RegisterDataSeries {
  contextType: GraphItemContextType.DataPoints;
}

export const isDataPoint = (v: RegisterDataSeries): v is DataPoint =>
  v.contextType === GraphItemContextType.DataPoints;

export interface RegisterDataSeriesWithBit extends RegisterDataSeries {
  valueBitMask: BitMask16;
}

export const hasBit = (v: RegisterDataSeries): v is RegisterDataSeriesWithBit =>
  v.valueBitMask != null;

interface Parameter extends RegisterDataSeries {
  contextType: GraphItemContextType.Parameters;
}

export const isParameter = (v: RegisterDataSeries): v is Parameter =>
  v.contextType === GraphItemContextType.Parameters;

export interface SelectedRegisterDataSeriesGraph {
  registerDataSeriesArray: RegisterDataSeries[];
  isFullResolution?: boolean;
}

export const emptySelectedRegisterDataSeriesGraphs: SelectedRegisterDataSeriesGraph[] = [];

export const configurationForDataPoint = (
  graphConfiguration: GraphConfiguration | undefined,
): Partial<RegisterDataSeries> => ({
  range: graphConfiguration?.range,
  resolution: graphConfiguration?.resolution,
  isForComparison: graphConfiguration?.withComparison,
});

export function getFinalAggregation(
  resolution: DataHistoryResolution | undefined,
  aggregation: DataHistoryAggregation | undefined,
  targetTab: TargetTab,
) {
  if (targetTab === TargetTab.Statistics) {
    return aggregation ?? DataHistoryAggregation.Avg; // Should always be set but just in case
  }
  if (resolution === DataHistoryResolution.All) {
    return DataHistoryAggregation.None;
  }
  if (aggregation != null && aggregation !== DataHistoryAggregation.None) {
    return aggregation;
  }
  return DataHistoryAggregation.Last;
}

export const dataHistoryConfigurationAsString = (
  resolution: DataHistoryResolution | undefined,
  aggregation: DataHistoryAggregation | undefined,
  desired?: boolean,
) => {
  const resolutionName = resolution != null
    ? Object.keys(DataHistoryResolution)[Object.values(DataHistoryResolution).indexOf(resolution)]
    : NotAvailable;
  const aggregationName = aggregation != null
    ? Object.keys(
      DataHistoryAggregation,
    )[Object.values(DataHistoryAggregation).indexOf(aggregation)]
    : NotAvailable;
  return `resolution: ${resolutionName}, ${
    desired ? 'desiredAggregation' : 'aggregation'
  }: ${aggregationName}`;
};

export const flatRegisterDataSeriesArrayFromGraphs = (graphs: SelectedRegisterDataSeriesGraph[]) =>
  flatten(graphs.map((graph) => graph.registerDataSeriesArray));

export const dataSeriesSort = (a: RegisterDataSeries, b: RegisterDataSeries) => {
  if (!!a.synthetic !== !!b.synthetic) {
    return a.synthetic ? 1 : -1;
  }
  let result = a.id - b.id;
  if (result === 0) {
    result = (a.valueBitMask ?? 0) - (b.valueBitMask ?? 0);
  }
  return result;
};

export const normalizeSelectedRegisterDataSeriesGraphs = (
  graphs: SelectedRegisterDataSeriesGraph[],
) =>
  graphs
    .map((graph): SelectedRegisterDataSeriesGraph => ({
      ...graph,
      registerDataSeriesArray: graph.registerDataSeriesArray.toSorted(dataSeriesSort),
    }))
    .filter((graph) => graph.registerDataSeriesArray.length > 0);

const dataSeriesExists = (
  registerIndex: RegisterDataSeries['registerIndex'],
  valueBitMask: RegisterDataSeries['valueBitMask'],
) =>
(item: RegisterDataSeries) =>
  item.registerIndex === registerIndex && item.valueBitMask == valueBitMask; // Also null == undefined

export const removeDataPoint = (
  updatedItems: SelectedRegisterDataSeriesGraph[],
  registerIndex: RegisterDataSeries['registerIndex'],
  valueBitMask: RegisterDataSeries['valueBitMask'],
  graphId: number | undefined,
) => {
  let selectedDataPoint: RegisterDataSeries | undefined;
  // graphId must be specified for Statistics tab but not for Diagrams tab.
  // This is because in Statistics tab you can choose a single data point to be displayed on multiple charts.
  const graphIndex = graphId ? graphId - 1 : updatedItems
    .findIndex((items) =>
      items.registerDataSeriesArray
        .some(dataSeriesExists(registerIndex, valueBitMask))
    );
  if (graphIndex >= 0) {
    const graph = updatedItems[graphIndex];
    const deletingIndex = graph.registerDataSeriesArray.findIndex(
      dataSeriesExists(registerIndex, valueBitMask),
    );
    if (deletingIndex >= 0) {
      [selectedDataPoint] = graph.registerDataSeriesArray.splice(deletingIndex, 1);
    }
  }
  return selectedDataPoint;
};
